import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import getErrorMessage from 'reduxModules/utils/getErrorMessage'
import notifications from '../../../global/notifications'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import CodeInput from '../CodeInput'
import InlineError, { errorId } from '../InlineError'
import RestrictedAccountNotice from '../RestrictedAccountNotice'
import './styles.css'

export const RESEND_COOLDOWN_SECONDS = 60
export const CODE_LENGTH = 6

/**
 * The "enter the 6-digit code" step, shared by the login page and the
 * passwordless create-account screens (SPEC-041, SPEC-101).
 *
 * The six-cell input submits on its own once the last digit lands (typed,
 * pasted, or autofilled); a wrong code keeps the digits in place, flagged, so
 * they can be corrected. The parent owns sending: `onResend` re-requests the
 * code, and every successful send bumps `sendCount`, which restarts the
 * (advisory) resend cooldown; the backend enforces the real one.
 */
export function OtpCodeStep({
  sentTo,
  sendCount,
  title = 'Check your email',
  subtitle,
  submitLabel = 'Log in',
  verifyCode,
  onResend,
  onBack,
  sending = false,
  canResend = true,
  captchaAppearance
}) {
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECONDS)
  const [code, setCode] = useState('')
  const [verifyError, setVerifyError] = useState(null)
  // SPEC-119 D3: a valid code on a terminated account
  const [restricted, setRestricted] = useState(false)
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    setSecondsLeft(RESEND_COOLDOWN_SECONDS)
  }, [sendCount])

  useEffect(() => {
    if (secondsLeft <= 0) return undefined
    const timer = setInterval(
      () => setSecondsLeft((seconds) => (seconds > 0 ? seconds - 1 : 0)),
      1000
    )
    return () => clearInterval(timer)
  }, [secondsLeft > 0])

  // Runs from the sixth digit landing or from the submit button
  const verify = (value) => {
    if (verifying || value.length !== CODE_LENGTH) return undefined
    setVerifyError(null)
    setVerifying(true)
    // Start inside a promise so a synchronous throw from the exchange still
    // lands in .catch/.finally and can't strand the button on "Verifying…".
    return Promise.resolve()
      .then(() => verifyCode(sentTo, value))
      .then((result) => {
        if (result.success) return
        if (result.restricted) setRestricted(true)
        else
          setVerifyError(
            getErrorMessage(
              result.payload,
              notifications.otpVerify.error.message
            )
          )
      })
      .catch(() => setVerifyError(notifications.otpVerify.error.message))
      .finally(() => setVerifying(false))
  }

  const onVerifySubmit = (event) => {
    event.preventDefault()
    verify(code)
  }

  const handleCodeChange = (digits) => {
    setCode(digits)
    if (verifyError) setVerifyError(null)
  }

  return (
    <div className="OtpCodeStep">
      <button
        type="button"
        className="otp-back"
        onClick={onBack}
        // A resend in flight would land on the parent's "sent" handler and
        // undo the back navigation, so hold Back until it settles.
        disabled={sending}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M15.4 5.6 9 12l6.4 6.4-1.4 1.4L6.2 12l7.8-7.8z"
          />
        </svg>
        Back
      </button>
      <h1 className="otp-title">{title}</h1>
      <p className="otp-subtitle">
        {subtitle || (
          <>
            Enter the 6-digit code we sent to <strong>{sentTo}</strong>.
          </>
        )}
      </p>
      <form onSubmit={onVerifySubmit} noValidate>
        <div className="otp-code-field">
          <label htmlFor="otp-code" className="field__label--hidden">
            6-digit code
          </label>
          <CodeInput
            id="otp-code"
            length={CODE_LENGTH}
            value={code}
            onChange={handleCodeChange}
            onComplete={verify}
            invalid={Boolean(verifyError)}
            disabled={verifying}
            autoFocus
            describedBy={errorId('otp-code')}
          />
          <InlineError id="otp-code" message={verifyError} />
          {restricted && <RestrictedAccountNotice />}
        </div>
        <CaptchaStandalone appearance={captchaAppearance} />
        <button
          type="submit"
          className="btn -wide -orange otp-submit"
          disabled={code.length !== CODE_LENGTH || verifying}
        >
          {verifying ? 'Verifying…' : submitLabel}
        </button>
      </form>
      <div className="otp-actions">
        <button
          type="button"
          className="otp-resend"
          onClick={onResend}
          disabled={secondsLeft > 0 || sending || !canResend}
        >
          {secondsLeft > 0 ? `Resend code (${secondsLeft}s)` : 'Resend code'}
        </button>
      </div>
    </div>
  )
}

OtpCodeStep.propTypes = {
  sentTo: PropTypes.string.isRequired,
  sendCount: PropTypes.number.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  submitLabel: PropTypes.string,
  verifyCode: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  sending: PropTypes.bool,
  canResend: PropTypes.bool,
  captchaAppearance: PropTypes.string
}

export default OtpCodeStep
